/**
 * A tool page's SOURCE and its RESULT, kept apart in the worker.
 *
 * ── THE DEFECT THIS REPLACES ──────────────────────────────────────────────
 * `apply` replaces a document under its own id (`rt.put(result.doc)` in
 * `src/worker/index.ts`), and the page used to apply every option change to
 * the id it already held. So each edit ran on the previous edit's output. Live
 * at build 31f48ec:
 *
 *   /shift-subtitles   +2500, then 00:00:10.000     → 13.500, not 11.000
 *   /sync-subtitles    anchors typed field by field  → 3.167/10.167/17.167,
 *                                                      not 2.000/5.500/9.000
 *   /subtitle-fps-converter  later rate changes ignored
 *   /add-pinyin-to-subtitles mode switches stacked
 *
 * Every option field is an absolute statement about the output ("shift by
 * 10 s"), so every run must start from the file as it was dropped.
 *
 * ── HOW A FRESH COPY IS MADE ──────────────────────────────────────────────
 * With `redecode`, the door that already exists. The tool page parses with
 * `retainBytes: true` so the encoding override can re-render, which means the
 * worker still holds the source bytes; `redecode` with the current encoding
 * reads them again into a NEW document whose diff baseline is the untouched
 * parse. No protocol change. The parse's own document is the first copy, so a
 * drop costs one parse, not two.
 *
 * The cost per option change is one re-parse beside the apply, diff,
 * availability projection and write the page already did on every change.
 *
 * ── OVERLAPPING RUNS ──────────────────────────────────────────────────────
 * A slider or a duration box emits a change per keystroke, so runs overlap.
 * Every run takes a generation number; one that a newer run (or a new file)
 * has overtaken frees what it made and reports `null`, and the page checks
 * `current()` after each await so a late answer never paints over a newer one.
 *
 * ── MORE THAN ONE FILE IN, MORE THAN ONE OUT ──────────────────────────────
 * The merge page takes several files. The first is the source as above; the
 * rest are `others`, parsed once and only ever READ (`mergeDocuments` resolves
 * them by id and never replaces them), so they need no fresh copies. The split
 * page gets part two back as a spawned document, which is kept beside the
 * result and freed with it, because the page offers it as a second download.
 *
 * ── THE FILE AS DROPPED, FOR A TABLE ──────────────────────────────────────
 * Every copy above is consumed by a run, so none of them still shows the file
 * once the options have done something. /sync-subtitles needs one that does:
 * its cue table is where the points' "Time in the file" come from, and a row
 * read off the result would give a time the first point had already moved.
 * `reference()` keeps one more redecode for that, made on first ask and freed
 * with the file.
 *
 * No DOM here, so `web/test/tool-source.test.mjs` drives it against the real
 * worker.
 */

import type { Diagnostic, DocumentId, InputId, Op } from '../../../../../contracts/cue-model.ts';
import type { ParsedInfo, ResultTypes } from '../../../../../src/core/protocol.ts';
import type { SubtitleWorkerClient } from '../../../../../src/worker/client.ts';

export type ToolSourceWorker = Pick<SubtitleWorkerClient, 'request' | 'dispose'>;

export interface ToolRun {
  readonly applied: ResultTypes['apply'];
  /**
   * What the engine said about THIS apply. An op it refuses comes back as an
   * 'invalid-op-parameters' error here and the document comes back unchanged,
   * so the page reads this before offering the result as a download.
   */
  readonly diagnostics: readonly Diagnostic[];
  /** False once a newer run or a new file has started. Check it after every await. */
  current(): boolean;
}

/**
 * The ops for one run, built once its fresh copy exists. A page whose ops
 * depend on the file itself (split's "before subtitle number", whose time is
 * read from the copy) passes this rather than a finished list.
 */
export type OpsFor = (copy: DocumentId) => Promise<readonly Op[]>;

/** The untouched file `reference()` keeps, and how many cues it has. */
export interface SourceReference {
  readonly docId: DocumentId;
  readonly cueCount: number;
}

export interface ToolSource {
  /** Take a fresh parse. Frees everything the previous file left in the worker. */
  load(info: ParsedInfo): void;
  /** Keep another parsed file for the ops to read, after the source. Merge only. */
  addOther(info: ParsedInfo): void;
  /** The other files' documents, in the order they were added. */
  others(): readonly DocumentId[];
  /** The encoding override changed: later copies decode the retained bytes with it. */
  setEncoding(label: string): void;
  /** Drop the current file and free its documents: a new drop, or a failed parse. */
  clear(): void;
  /** Forget ids without freeing them, for a respawned worker that holds none of them. */
  forget(): void;
  /** Run `ops` over a fresh copy of the source. `null` when superseded or nothing is loaded. */
  run(ops: readonly Op[] | OpsFor): Promise<ToolRun | null>;
  /**
   * The file as dropped, kept untouched beside the runs, for a table that shows the file
   * rather than the result: /sync-subtitles, where a row's time is the time in the file.
   * Made on the first ask from the retained bytes, once per file and encoding. A new file, a
   * new encoding or a clear frees it. `null` when nothing is loaded, or when one of those
   * happened while it was being made.
   */
  reference(): Promise<SourceReference | null>;
  readonly inputId: InputId | null;
  /** Everything held, for `pagehide`. */
  held(): { readonly docIds: readonly DocumentId[]; readonly inputIds: readonly InputId[] };
}

export function createToolSource(client: ToolSourceWorker): ToolSource {
  let inputId: InputId | null = null;
  let encodingLabel: string | null = null;
  /** An untouched parse no run has consumed yet. */
  let pristine: DocumentId | null = null;
  /** The last finished run's document: what the page shows and downloads. */
  let result: DocumentId | null = null;
  /** What that run spawned (split's part two), freed when the result is. */
  let spawned: readonly DocumentId[] = [];
  /** The other files, for a merge: read by the ops, never replaced by them. */
  let others: readonly { readonly docId: DocumentId; readonly inputId: InputId }[] = [];
  let generation = 0;
  /** The file as dropped, for `reference()`, and the redecode making it. */
  let reference: SourceReference | null = null;
  let referencing: Promise<SourceReference | null> | null = null;
  /**
   * Bumped whenever the file under a reference changes: a new file, an encoding, a clear.
   * Separate from `generation`, which every run bumps; a run does not change the file.
   */
  let sourceEpoch = 0;

  function free(ids: readonly (DocumentId | null)[]): void {
    const live = ids.filter((id): id is DocumentId => id !== null);
    if (live.length > 0) client.dispose(live);
  }

  function release(): void {
    generation += 1;
    free([pristine, result, reference?.docId ?? null, ...spawned, ...others.map((o) => o.docId)]);
    const inputs = [...(inputId === null ? [] : [inputId]), ...others.map((o) => o.inputId)];
    if (inputs.length > 0) client.dispose([], inputs);
    forget();
  }

  /** Let go of the reference without freeing it: the caller has, or the worker never held it. */
  function dropReference(): void {
    sourceEpoch += 1;
    reference = null;
    referencing = null;
  }

  function forget(): void {
    inputId = null;
    encodingLabel = null;
    pristine = null;
    result = null;
    spawned = [];
    others = [];
    dropReference();
  }

  async function freshCopy(input: InputId, label: string): Promise<DocumentId> {
    if (pristine !== null) {
      const id = pristine;
      pristine = null;
      return id;
    }
    const info = await client.request('redecode', { inputId: input, encodingLabel: label });
    return info.docId;
  }

  return {
    load(info) {
      release();
      inputId = info.inputId;
      encodingLabel = info.encoding.label;
      pristine = info.docId;
    },

    addOther(info) {
      others = [...others, { docId: info.docId, inputId: info.inputId }];
    },

    others() {
      return others.map((o) => o.docId);
    },

    setEncoding(label) {
      encodingLabel = label;
      // The waiting copy and the reference were decoded with the old label.
      free([pristine, reference?.docId ?? null]);
      pristine = null;
      dropReference();
    },

    clear: release,

    forget() {
      generation += 1;
      forget();
    },

    async run(opsOrBuild) {
      if (inputId === null || encodingLabel === null) return null;
      const mine = ++generation;
      const current = () => mine === generation;
      let fresh: DocumentId | null = null;
      try {
        fresh = await freshCopy(inputId, encodingLabel);
        if (!current()) {
          free([fresh]);
          return null;
        }
        const ops = typeof opsOrBuild === 'function' ? await opsOrBuild(fresh) : opsOrBuild;
        if (!current()) {
          free([fresh]);
          return null;
        }
        let diagnostics: readonly Diagnostic[] = [];
        const onDiagnostics = (d: readonly Diagnostic[]) => {
          diagnostics = d;
        };
        const applied = await client.request('apply', { docId: fresh, ops }, { onDiagnostics });
        // An op may hand back a different id. The copy it replaced is freed; what the run
        // SPAWNED (split's part two) is kept with the result, because the page offers it as a
        // download of its own. Freeing it here is how part two used to vanish.
        const made = [fresh].filter((id) => id !== applied.docId);
        if (!current()) {
          free([applied.docId, ...applied.spawned, ...made]);
          return null;
        }
        // The previous result goes only now that its replacement exists, so the cue table
        // always has a document to page through.
        const previous = [result, ...spawned].filter(
          (id) => id !== applied.docId && !applied.spawned.includes(id as DocumentId),
        );
        result = applied.docId;
        spawned = applied.spawned;
        free([...made, ...previous]);
        return { applied, diagnostics, current };
      } catch (err) {
        // Nothing after `apply` awaits, so a throw means the copy was never adopted.
        free([fresh]);
        // A run overtaken by a new file can fail because that file freed its input. That is
        // a failure about a document nobody is looking at.
        if (!current()) return null;
        throw err;
      }
    },

    reference() {
      if (reference !== null) return Promise.resolve(reference);
      if (referencing !== null) return referencing;
      if (inputId === null || encodingLabel === null) return Promise.resolve(null);
      const mine = sourceEpoch;
      const making = client.request('redecode', { inputId, encodingLabel }).then(
        (info) => {
          if (mine !== sourceEpoch) {
            // Made for a file or an encoding that has since been replaced.
            free([info.docId]);
            return null;
          }
          reference = { docId: info.docId, cueCount: info.cueCount };
          return reference;
        },
        (err: unknown) => {
          // A new file freed the input this was reading. Nobody is looking at it.
          if (mine !== sourceEpoch) return null;
          throw err;
        },
      );
      referencing = making;
      const settled = () => {
        if (referencing === making) referencing = null;
      };
      making.then(settled, settled);
      return making;
    },

    get inputId() {
      return inputId;
    },

    held() {
      return {
        docIds: [
          pristine,
          result,
          reference?.docId ?? null,
          ...spawned,
          ...others.map((o) => o.docId),
        ].filter((id): id is DocumentId => id !== null),
        inputIds: [...(inputId === null ? [] : [inputId]), ...others.map((o) => o.inputId)],
      };
    },
  };
}
